import type { AccountDetailDto, TransactionDto } from "@home/shared";
import { useState } from "react";
import { api } from "../api.ts";
import { money } from "../format.ts";
import { t, type Lang } from "../i18n.ts";
import { errorText } from "../meta.ts";
import { hapticTap } from "../telegram.ts";
import { invalidateRemote, useRemote } from "../useRemote.ts";
import { AmountField } from "./AmountField.tsx";
import { PrimaryAction, Sheet } from "./Sheet.tsx";
import { toast } from "./Toast.tsx";
import { TxDetailSheet } from "./TxDetailSheet.tsx";
import { TxList } from "./TxList.tsx";

type Part = keyof AccountDetailDto["parts"];

/** Строки разбивки: подпись и знак, с которым часть входит в остаток. */
const PARTS: { key: Part; label: Parameters<typeof t>[0]; sign: 1 | -1 }[] = [
  { key: "opening", label: "partOpening", sign: 1 },
  { key: "income", label: "partIncome", sign: 1 },
  { key: "expense", label: "partExpense", sign: -1 },
  { key: "transfersIn", label: "partTransfersIn", sign: 1 },
  { key: "transfersOut", label: "partTransfersOut", sign: -1 },
  { key: "credits", label: "partCredits", sign: 1 },
  { key: "goals", label: "partGoals", sign: -1 },
];

/** Счёт подробно: остаток и из чего он сложился, «Сверить остаток», переименование, заначка, операции. */
export function AccountSheet({ id, lang, onClose }: { id: string; lang: Lang; onClose: () => void }) {
  const remote = useRemote(`account:${id}`, () => api.account(id));
  const [mode, setMode] = useState<"view" | "reconcile" | "rename">("view");
  const [value, setValue] = useState("");
  // Карта ушла в минус (овердрафт): поле суммы минус не принимает — отдельная отметка.
  const [negative, setNegative] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<TransactionDto | null>(null);
  const a = remote.data;

  const start = (next: "reconcile" | "rename") => {
    if (!a) return;
    setError(null);
    setValue(next === "rename" ? a.name : "");
    setNegative(false);
    setMode(next);
  };

  const run = async (action: () => Promise<unknown>, done: string) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await action();
      hapticTap();
      toast(done);
      invalidateRemote();
      setMode("view");
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const submit = () => {
    if (!a) return;
    if (mode === "rename") {
      if (!value.trim()) return setError(t("fillName"));
      return void run(() => api.renameAccount(a.id, value.trim()), t("savedOk"));
    }
    // Ноль — тоже остаток: «на карте пусто».
    const actual = Number(value.replace(/[\s ]/g, ""));
    if (value.trim() === "" || !Number.isFinite(actual)) return setError(t("fillAmount"));
    return void run(() => api.setAccountBalance(a.id, negative ? -actual : actual), t("reconciledOk"));
  };

  const toggleStash = () => {
    if (!a) return;
    void run(() => api.setAccountStash(a.id, !a.stash), a.stash ? t("stashOffOk") : t("stashOnOk"));
  };

  return (
    <Sheet title={a ? `${a.stash ? "🐷" : a.currency === "USD" ? "💵" : "💳"} ${a.name}` : t("accountsTitle")} onClose={onClose}>
      {!a ? (
        <div className="skeleton skeleton-block" />
      ) : (
        <>
          <div className="tx-hero">
            <div className={a.balance < 0 ? "tx-hero-amount negative" : "tx-hero-amount"}>{money(a.balance, a.currency, lang)}</div>
            <div className="hint">
              {[a.isDefault ? t("defaultBadge") : null, a.stash ? t("stashSection") : a.personal ? `🔒 ${t("personalAcc")}` : null].filter(Boolean).join(" · ")}
            </div>
          </div>

          {mode === "view" ? (
            <>
              <section className="dash-card">
                <h3>{t("balanceParts")}</h3>
                <ul className="plain-list">
                  {PARTS.filter((p) => a.parts[p.key] !== 0 || p.key === "opening").map((p) => (
                    <li key={p.key}>
                      <span>{t(p.label)}</span>
                      <b className={p.sign * a.parts[p.key] > 0 ? "income" : ""}>
                        {money(p.sign * a.parts[p.key], a.currency, lang, p.sign * a.parts[p.key] > 0)}
                      </b>
                    </li>
                  ))}
                </ul>
              </section>
              <div className="field-row">
                <button type="button" className="btn" onClick={() => start("reconcile")}>
                  ⚖️ {t("reconcile")}
                </button>
                <button type="button" className="btn" onClick={() => start("rename")}>
                  ✏️ {t("renameAccount")}
                </button>
              </div>
              {/* Заначкой может стать только свой личный счёт (не основной). */}
              {a.personal && !a.isDefault && (
                <button type="button" className="btn btn-block" disabled={busy} onClick={toggleStash}>
                  {a.stash ? t("unmakeStash") : t("makeStash")}
                </button>
              )}
              <h3 className="section-title">{t("accountOps")}</h3>
              {a.operations.length === 0 ? (
                <p className="hint">{t("noAccountOps")}</p>
              ) : (
                <TxList items={a.operations} lang={lang} perspective={a.id} onPick={setPicked} />
              )}
            </>
          ) : (
            <div className="form add-form">
              {mode === "reconcile" ? (
                <>
                  <AmountField value={value} onChange={setValue} currency={a.currency} lang={lang} label={t("actualBalance")} autoFocus />
                  <label className="toggle">
                    <input type="checkbox" checked={negative} onChange={(e) => setNegative(e.target.checked)} />
                    <span>{t("negativeBalance")}</span>
                  </label>
                  <p className="hint">{t("reconcileHint")}</p>
                </>
              ) : (
                <label className="field">
                  <span>{t("accountName")}</span>
                  <input className="input" autoFocus value={value} maxLength={60} onChange={(e) => setValue(e.target.value)} />
                </label>
              )}
              <button type="button" className="btn-link" onClick={() => setMode("view")}>
                {t("cancel")}
              </button>
            </div>
          )}
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          {mode !== "view" && <PrimaryAction label={t("save")} onClick={submit} busy={busy} />}
        </>
      )}
      {picked && <TxDetailSheet tx={picked} lang={lang} onClose={() => setPicked(null)} />}
    </Sheet>
  );
}
